import React from "react";
import { type StaticImageData, type ImageProps as NextImageProps } from "next/image";
import { AspectRatio, type AspectRatioProps, Box, SimpleGrid, type SpaceProps } from "@chakra-ui/react";
import { FillImage } from "components/images";
import { desktopOnlyDisplay, mobileOnlyDisplay } from "utils";
import { TextTitle, type TextTitleProps } from "ui/text";
import { type BrandColorToken } from "theme/colors";

function MobileBlend({ bgColor }: { bgColor: BrandColorToken }) {
  return (
    <Box
      display={mobileOnlyDisplay()}
      pos="absolute"
      right={0}
      bottom={0}
      left={0}
      height="bigger"
      bgGradient={`linear(to-t, ${bgColor}, transparent)`}
    />
  );
}

function DesktopBlend({ bgColor }: { bgColor: BrandColorToken }) {
  return (
    <Box
      display={desktopOnlyDisplay()}
      pos="absolute"
      top={0}
      right={0}
      bottom={0}
      width="bigger"
      bgGradient={`linear(to-l, ${bgColor}, transparent)`}
    />
  );
}

const defaultImageRatio = 2 / 1;
const imageStyle: NextImageProps["style"] = { objectFit: "cover", objectPosition: "center" };

type ImageProps = {
  /**
   * The image to render in the card.
   */
  src: StaticImageData;

  /**
   * The alt text for the image.
   */
  alt: string;

  /**
   * The ratio of the image.
   *
   * @default 2 / 1
   */
  ratio?: AspectRatioProps["ratio"];
};

type HorizontalCardProps = {
  /**
   * The theme of the card.
   *
   * @default "light"
   */
  theme?: TextTitleProps["theme"];

  /**
   * The background color of the card.
   *
   * @default "brand.backgroundSecondary.light"
   */
  bgColor?: BrandColorToken;

  /**
   * The card image.
   */
  image: ImageProps;

  /**
   * Optional image to render on mobile screens.
   */
  mobileImage?: ImageProps;

  /**
   * The title of the card.
   */
  title: TextTitleProps["title"];

  /**
   * The html element to use for the title.
   *
   * @default "h3"
   */
  titleAs?: TextTitleProps["titleAs"];

  /**
   * Optional description of the card.
   */
  description?: TextTitleProps["description"];

  /**
   * The html element to use for the description.
   *
   * @default "p"
   */
  descriptionAs?: TextTitleProps["descriptionAs"];

  /**
   * The maximum width of the text content.
   *
   * @default 380px
   */
  textMaxW?: TextTitleProps["maxW"];

  /**
   * Optional actions to render in the card.
   */
  actions?: TextTitleProps["actions"];
} & SpaceProps;

/**
 * A light-themed, secondary fill-style card.
 * On desktop, media and text are arranged horizontally and includes an aura bg behind the media.
 * On mobile, media and text are arranged vertically, and no aura bg is shown.
 */
export function HorizontalCard({
  theme = "light",
  bgColor = "new.gray.50",
  image,
  mobileImage,
  title,
  titleAs = "h3",
  description,
  descriptionAs = "p",
  actions,
  textMaxW = "380px",
  ...spaceProps
}: HorizontalCardProps) {
  const imageRatio = image.ratio ?? defaultImageRatio;
  const mobileImageRatio = mobileImage?.ratio ?? imageRatio;

  return (
    <Box
      pos="relative"
      borderRadius={{ base: "big", sm: "bigger" }}
      bg={bgColor}
      overflow="hidden"
      maxW={{ base: "100%", sm: "cardMaxW", md: "100%" }}
      mx="auto"
      {...spaceProps}
    >
      <SimpleGrid pos="relative" columns={{ base: 1, md: 2 }} alignItems="center">
        {mobileImage && (
          <AspectRatio
            display={mobileOnlyDisplay()}
            ratio={mobileImageRatio}
            pos="relative"
            h="100%"
            maxH="100%"
            w="auto"
          >
            <Box boxSize="100%" pos="relative">
              <FillImage src={mobileImage.src} alt={mobileImage.alt} style={imageStyle} />
              {/* Blend the image into the edges of the card. */}
              <MobileBlend bgColor={bgColor} />
            </Box>
          </AspectRatio>
        )}

        <AspectRatio
          display={mobileImage ? desktopOnlyDisplay() : "block"}
          ratio={imageRatio}
          pos="relative"
          h="100%"
          maxH="100%"
          w="auto"
        >
          <Box boxSize="100%" pos="relative">
            <FillImage src={image.src} alt={image.alt} style={imageStyle} />
            {/* Blend the image into the edges of the card to avoid harsh cutoffs as it resizes. */}
            <MobileBlend bgColor={bgColor} />
            <DesktopBlend bgColor={bgColor} />
          </Box>
        </AspectRatio>

        <Box p={{ base: "bigger", lg: "biggest", xl: "huge" }}>
          <TextTitle
            size="xs"
            theme={theme}
            maxW={textMaxW}
            title={title}
            titleAs={titleAs}
            description={description}
            descriptionAs={descriptionAs}
            actions={actions}
          />
        </Box>
      </SimpleGrid>
    </Box>
  );
}
